import { useState, useEffect } from 'react'
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@cutilei/ui'
import AuthService from 'services/auth'
import cutileiApi from 'services/cutileiApi'
import { logApiError } from 'services/apiError'
import ScheduleService from 'services/Schedule'
import NumberInput from 'components/Inputs/NumberInput'
import MoneyInput from 'components/Inputs/MoneyInput'
import Select from 'components/Inputs/Select'
import Tooltip from 'components/Tooltip'
import MainButton from 'components/Buttons/MainButton'
import SecondaryButton from 'components/Buttons/SecondaryButton'
import submitOnEnter from 'utils/submitOnEnter'
import editServiceValidator from 'validators/editServiceValidator'
import { FaPlus, FaCheck, FaTrash } from 'react-icons/fa'
import { Formik } from 'formik'

import {
  Form,
  StepRow,
  StepContainer,
  StepIndicator,
  StepLabel,
  StepNumber,
  StepLineContainer,
  StepLineBackground,
  StepLineProgress,
  FormField,
  ProductGrid,
  Row,
  SubTitle,
  InfoText,
  HighlightText,
  Label,
  WarningText,
  ErrorContainer
} from './styles'

export default function CutileiServicesConfigModal ({
  visible,
  services = [],
  businessProducts = [],
  onConfirm: handleConfirm,
  onClose: handleClose
}) {
  const [currentStep, setCurrentStep] = useState (0)
  const [completedServices, setCompletedServices] = useState ([])
  const [isAnimating, setIsAnimating] = useState (false)
  const [enableTransition, setEnableTransition] = useState (true)
  const [slideDirection, setSlideDirection] = useState (0)
  const [errorMessage, setErrorMessage] = useState (null)
  const businessId = AuthService.getBusinessId ()
  const token = AuthService.getToken ()
  const currentService = services[currentStep]

  const requestConfig = {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  }
  useEffect (() => {
    if (visible) {
      const isServiceConfigured = service => service.comission_percentage > 0 && service.duration > 0
      const firstUnconfiguredIndex = services.findIndex (service => !isServiceConfigured (service))
      const initialStep = firstUnconfiguredIndex === -1 ? 0 : firstUnconfiguredIndex
      const alreadyCompleted = services.slice (0, initialStep)

      setCurrentStep (initialStep)
      setCompletedServices (alreadyCompleted)
      setIsAnimating (false)
      setSlideDirection (0)
      setErrorMessage (null)
    }
  }, [visible, services])

  const animateAndChangeStep = (newStep, direction) => {
    setSlideDirection (direction)
    setIsAnimating (true)
    
    setTimeout (() => {
      setCurrentStep (newStep)
      setEnableTransition (false)
      setSlideDirection (-direction)
      
      requestAnimationFrame (() => {
        requestAnimationFrame (() => {
          setEnableTransition (true)
          setSlideDirection (0)
          setIsAnimating (false)
        })
      })
    }, 300)
  }

  const goToNextStep = () => animateAndChangeStep (currentStep + 1, -1)
  const goToPreviousStep = () => animateAndChangeStep (currentStep - 1, 1)

  const handleServiceSubmit = async (values, { setSubmitting }) => {
    setErrorMessage (null)
    setSubmitting (true)
  
    try {
      const data = {...values, comission_percentage: values.comission_percentage / 100}
  
      await cutileiApi.put (
        `/businesses/${businessId}/services/${values.id}`, data, requestConfig
      )
  
      setSubmitting (false)

      const updatedService = {...currentService, ...data}
      setCompletedServices ([...completedServices, updatedService])

      if (currentStep < services.length - 1) {
        goToNextStep ()
      } else {
        handleConfirm ([...completedServices, updatedService])
        handleClose ()
      }
    } catch (error) {
      setSubmitting (false)
      setErrorMessage ('Erro ao salvar serviço. Tente novamente.')
      logApiError (error)
    }
  }

  if (!currentService) {
    return null
  }

  const progressPercentage = services.length > 1
    ? (currentStep / (services.length - 1)) * 100 
    : 0

  const formAnimationStyle = {
    transform: `translateX(${slideDirection * 50}px)`,
    opacity: isAnimating && slideDirection !== 0 ? 0 : 1,
    transition: enableTransition 
      ? 'transform 0.3s ease-in-out, opacity 0.3s ease-in-out'
      : 'none'
  }

  return (
    <Dialog open={visible}>
      <DialogContent
        size='lg'
        showCloseButton={false}
        closeOnOutsideClick={false}
        aria-describedby={undefined}
        // Só fecha no fim do passo a passo: sem X, sem Esc e sem clique fora
        onEscapeKeyDown={event => event.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>Configurar serviços Cutilei</DialogTitle>
        </DialogHeader>
        {/* Os passos ficam parados entre o título e o corpo, que rola */}
        <div style={{paddingInline: 24}}>
          <StepRow>
            <StepLineContainer>
              <StepLineBackground/>
              <StepLineProgress progress={progressPercentage} />
            </StepLineContainer>
            {services.map ((service, index) => {
              const isActive = index === currentStep
              const isCompleted = index < currentStep

              return (
                <StepContainer key={service.id}>
                  <StepIndicator active={isActive} isCompleted={isCompleted}>
                    {isCompleted ? (
                      <FaCheck color='#FFFFFF' size={14}/>
                    ) : (
                      <StepNumber>{index + 1}</StepNumber>
                    )}
                  </StepIndicator>
                  <StepLabel>
                    {service.name}
                  </StepLabel>
                </StepContainer>
              )
            })}
          </StepRow>
        </div>
        <Formik
          enableReinitialize={true}
          validationSchema={editServiceValidator}
          initialValues={{
            'id': currentService.id,
            'nickname': currentService.nickname || currentService.name,
            'description': currentService.description || '',
            'duration': currentService.duration,
            'max_delay': currentService.max_delay,
            'price': currentService.price,
            'comission_percentage': currentService.comission_percentage * 100,
            'professional_comission': currentService.comission_percentage * currentService.price ?? 0,
            'business_comission': currentService.price - (currentService.comission_percentage * currentService.price ?? 0),
            'cutilei_service': currentService.cutilei_service,
            'accepts_online': currentService.accepts_online,
            'products': currentService.products ?? []
          }}
          onSubmit={handleServiceSubmit}
        >
          {function ConfigCutileiServiceForm ({
            values,
            errors,
            touched,
            isSubmitting,
            setFieldValue,
            setFieldTouched,
            handleBlur, 
            handleSubmit
          }) {
            useEffect (() => {
              recalcutaleComissionValue (values)
            }, [values.price])

            useEffect (() => {
              setFieldValue ('business_comission', Math.abs (values.price - values.professional_comission))
            }, [values.price, values.comission_percentage, values.professional_comission])

            const handleAddProduct = () => {
              setFieldValue ('products', [
                ...values.products, {
                  id: '',
                  name: '',
                  quantity_used: 1
                }
              ])
            }

            const handleDeleteProduct = index => {
              setFieldValue (
                'products',
                values.products.map ((p, i) => ({...p, idx: i})).filter (s => s.idx !== index)
              )
            }

            const calculateComissionValue = (price, comissionPercentage) => {
              return Math.round (price * (comissionPercentage / 100 || 0) * 100) / 100
            }

            const calculateComissionPercentage = (price, comissionValue) => {
              return Math.round ((comissionValue / price) * 100 * 100) / 100
            }

            const recalcutaleComissionValue = values => {
              const comissionValue = calculateComissionValue (values.price, values.comission_percentage)
              setFieldValue ('professional_comission', comissionValue)
            }

            return (
              <>
                <DialogBody>
                  <Form onKeyDown={submitOnEnter(handleSubmit, isSubmitting)} style={formAnimationStyle}>
                    <SubTitle>
                      {currentService.name}
                    </SubTitle>
                    <InfoText>
                      Configure o <HighlightText>tempo do serviço</HighlightText>
                    </InfoText>
                    <Row style={{
                      display: 'grid',
                      gridTemplateColumns: values.cutilei_service ? '1fr 1fr' : '2fr 1fr 1fr'
                    }}>
                      <FormField>
                        <Label htmlFor='duration'>
                          Tempo do serviço (mins) {values.duration >= 60 && ` - ${
                            ScheduleService.getFormattedDuration (values.duration)
                          }`}
                        </Label>
                        <NumberInput
                          id='duration'
                          name='duration'
                          value={values.duration}
                          minValue={15}
                          stepOnly={true}
                          step={values.duration < 60 ? 15 : 30}
                          onChange={(name, value) => {
                            const newValue = values.duration === 60 && values.duration > value ? 45 : value
                            setFieldValue (name, newValue)
                          }}
                          style={{width: '100%'}}
                          // A borda verde marca a duração alterada; sem alteração, fica a borda da lib
                          inputStyle={values.duration !== currentService.duration ? { borderColor: '#44CF6C' } : undefined}
                        />
                      </FormField>
                      <FormField>
                        <Row style={{margin: 0, gap: 3, alignItems: 'center'}}>
                          <Label htmlFor='max_delay'>
                            Tolerância de atraso (mins)
                          </Label>
                          <Tooltip
                            id='max_delay_tooltip'
                            place='bottom'
                            message={
                              <Label style={{color: '#FFFFFF', width: 300}}>
                                A tolerância de atraso pode ser configurada da seguinte forma:<br/><br/>
                                Serviços do salão: 5 a 20 minutos<br/>
                                Serviços de Pé e mão Cutilei: 10 a 15 minutos<br/>
                                Demais Serviços do Cutilei: 7 a 15 minutos<br/>
                              </Label>
                            }
                          />
                        </Row>
                        <NumberInput
                          id='max_delay'
                          name='max_delay'
                          value={values.max_delay}
                          minValue={currentService.name.toLowerCase () === 'pé e mão' ? 10 : 7}
                          maxValue={15}
                          onChange={setFieldValue}
                          style={{width: '100%'}}
                        />
                      </FormField>
                    </Row>
                    <Row style={{alignItems: 'flex-start'}}>
                      <FormField>
                        <Label htmlFor='price'>
                          Preço (R$)
                        </Label>
                        <MoneyInput
                          id='price'
                          name='price'
                          placeholder='Preço'
                          value={values.price}
                          onValueChange={setFieldValue}
                          onBlur={handleBlur ('price')}
                          disabled={true}
                        />
                        {touched.price && errors.price && (
                          <WarningText>
                            {errors.price}
                          </WarningText>
                        )}
                      </FormField>
                      <FormField>
                        <Label htmlFor='comission_percentage'>
                          Comissão (%)
                        </Label>
                        <NumberInput
                          id='comission_percentage'
                          name='comission_percentage'
                          placeholder='Comissão (%)'
                          value={values.comission_percentage}
                          minValue={0}
                          maxValue={100}
                          onChange={(name, value) => {
                            setFieldValue (name, value)
                            setFieldValue ('professional_comission', calculateComissionValue (values.price, value))
                          }}
                          onBlur={handleBlur}
                        />
                      </FormField>
                      <FormField>
                        <Label htmlFor='professional_comission'>
                          Comissão (profissional)
                        </Label>
                        <MoneyInput
                          id='professional_comission'
                          name='professional_comission'
                          placeholder='Comissão (profissional)'
                          value={values.professional_comission}
                          maxValue={values.price}
                          onValueChange={(name, value) => {
                            setFieldValue (name, value)
                            setFieldValue ('comission_percentage', calculateComissionPercentage (values.price, value))
                          }}
                          onBlur={handleBlur ('professional_comission')}
                          style={{color: '#44CF6C', fontWeight: 600}}
                        />
                      </FormField>
                      <FormField>
                        <Label htmlFor='business_comission'>
                          Valor (salão)
                        </Label>
                        <MoneyInput
                          id='business_comission'
                          name='business_comission'
                          value={values.business_comission}
                          style={{color: '#44CF6C', fontWeight: 600}}
                          disabled={true}
                        />
                      </FormField>
                    </Row>
                    {values.products.length > 0 && values.products.map ((product, index) => (
                      <ProductGrid key={index}>
                        <FormField>
                          <Label>Produto</Label>
                          <Select
                            name={`products.${index}.id`}
                            placeholder='Selecionar...'
                            value={product.id ? {
                              label: product.nickname || product.name,
                              value: product.id
                            } : null}
                            options={businessProducts}
                            onChange={(name, option) => {
                              setFieldValue (name, option.value.id)
                              setFieldValue (`products.${index}.name`, option.value.nickname || option.value.name)
                            }}
                            onBlur={setFieldTouched}
                            error={errors.products?.[index]?.name}
                            touched={touched.products?.[index]?.name}
                          />
                          {touched.products?.[index]?.name && errors.products?.[index]?.name && (
                            <WarningText>
                              {errors.products?.[index]?.name}
                            </WarningText>
                          )}
                        </FormField>
                        <Row style={{margin: 0}}>
                          <FormField>
                            <Label>Qtd. utilizada</Label>
                            <NumberInput
                              name={`products.${index}.quantity_used`}
                              value={product.quantity_used}
                              minValue={1}
                              maxValue={999999}
                              onChange={setFieldValue}
                            />
                          </FormField>
                          {product.id && (
                            <InfoText style={{marginBottom: 8}}>
                              {businessProducts.find (p => p.value.id === product.id)?.value.content_unit}
                            </InfoText>
                          )}
                        </Row>
                        <SecondaryButton
                          onClick={() => handleDeleteProduct (index)}
                          leftIcon={<FaTrash color='#FFFFFF' size={12}/>}
                          width={50}
                          color='#FF3939'
                        />
                      </ProductGrid>
                    ))}
                    <SecondaryButton
                      label='Adicionar produto'
                      onClick={handleAddProduct}
                      leftIcon={<FaPlus size={12} color='#FFC100' style={{marginBottom: 1}}/>}
                      width={180}
                    />
                    {errorMessage && (
                      <ErrorContainer>
                        <p>{errorMessage}</p>
                      </ErrorContainer>
                    )}
                  </Form>
                </DialogBody>
                <DialogFooter>
                  <Row style={{gap: 10, margin: '5px 0px 0px'}}>
                    {currentStep > 0 && (
                      <SecondaryButton
                        label='Voltar'
                        onClick={goToPreviousStep}
                        disabled={isSubmitting}
                        width={140}
                      />
                    )}
                    <MainButton
                      label={currentStep < services.length - 1 ? 'Próximo' : 'Finalizar'}
                      onClick={handleSubmit}
                      disabled={isSubmitting}
                      loading={isSubmitting}
                      width={140}
                    />
                  </Row>
                </DialogFooter>
              </>
            )
          }}
        </Formik>
      </DialogContent>
    </Dialog>
  )
}
